import React, { useEffect, useRef, useState } from 'react';
import useFixedDropdownPosition from './useFixedDropdownPosition';

// Searchable multi-select. Generalised out of SheetsFilters' MultiValSelect, which was
// hardcoded to flat string options and the filter-row styles; MultiValSelect is now a
// thin wrapper over this so there is one implementation, not two.
//
// Positioned fixed (useFixedDropdownPosition) so the menu isn't clipped by the
// AgentPopup or the settings-modal body — both hosts scroll, and both are where these
// pickers live.

const BASE = {
  trigger: {
    width: '100%', padding: '7px 9px', fontSize: '13px',
    border: '1px solid var(--border,#e2e8f0)', borderRadius: 'var(--radius,6px)',
    background: '#fff', color: 'var(--text-primary,#0f172a)',
  },
};

/**
 * `options` are `{ value, label, hint? }` — `hint` becomes the row's title tooltip.
 * `value` is the array of selected `option.value`s; `onChange` receives the next array.
 * `styles` is the host editor's style object; only `styles.input` is consulted, and it
 * falls back to a self-contained look so this works uninstrumented.
 * `selectedFirst` lists the selected options at the top of the menu, ordered by the
 * selection as it was when the menu opened, so rows don't jump while boxes are ticked.
 */
export default function SearchableMultiSelect({
  value = [],
  options = [],
  onChange,
  styles,
  disabled = false,
  placeholder = '— Select —',
  emptyText = 'No matches',
  summarize,
  selectedFirst = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The selection at open time; drives the selectedFirst ordering.
  const [pinned, setPinned] = useState(() => new Set());
  const ref = useRef(null);
  const searchRef = useRef(null);
  const menuPos = useFixedDropdownPosition(ref, open, 260);

  useEffect(() => {
    if (!open) { setSearch(''); return undefined; }
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const ordered = selectedFirst
    ? [
      ...options.filter((o) => pinned.has(String(o.value))),
      ...options.filter((o) => !pinned.has(String(o.value))),
    ]
    : options;
  const q = search.trim().toLowerCase();
  const filtered = q
    ? ordered.filter((o) => String(o.label).toLowerCase().includes(q))
    : ordered;

  const selected = new Set(value.map(String));
  const firstLabel = options.find((o) => selected.has(String(o.value)))?.label;
  const label = value.length === 0
    ? placeholder
    : value.length === 1
      ? (firstLabel ?? `${value[0]}`)
      : (summarize ? summarize(value.length) : `${value.length} selected`);

  const toggle = (v) => {
    if (disabled) return;
    const key = String(v);
    onChange(selected.has(key) ? value.filter((x) => String(x) !== key) : [...value, v]);
  };

  const toggleOpen = () => {
    if (disabled) return;
    if (!open) setPinned(new Set(value.map(String)));
    setOpen((o) => !o);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={toggleOpen}
        disabled={disabled}
        style={{
          ...(styles?.input || BASE.trigger),
          display: 'flex', alignItems: 'center', textAlign: 'left',
          cursor: disabled ? 'default' : 'pointer',
          background: disabled ? '#f1f5f9' : '#fff',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: '9px', color: 'var(--text-muted)', flexShrink: 0, marginLeft: '4px' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          ...(menuPos || { position: 'fixed', visibility: 'hidden' }),
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: '1px solid var(--border,#e2e8f0)', borderRadius: 'var(--radius,6px)', background: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border,#e2e8f0)', flexShrink: 0 }}>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', padding: '5px 7px', fontSize: '12px',
                border: '1px solid var(--border,#e2e8f0)', borderRadius: '4px',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: '12px', color: 'var(--text-muted)' }}>{emptyText}</div>
            )}
            {filtered.map((o) => (
              <label
                key={o.value}
                title={o.hint || undefined}
                style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(String(o.value))}
                  onChange={() => toggle(o.value)}
                  style={{ accentColor: 'var(--primary)', flexShrink: 0 }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}